/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import AssertionError from 'assertion-error';
import isNil from 'lodash/isNil';

import ScheduleContent, { ContentType } from '../../schedule/schedule-content';
import ScheduleInfo from '../../schedule/schedule-info';
import { EmptyContentController } from './empty-content-controller';
import { LayoutContentController } from './layout-content-controller';
import { MediumContentController } from './medium-content-controller';
import MediumExternalContentController from './medium-external-content-controller';
import { PlaylistContentController } from './playlist-content-controller';
import { PlaylistDeterministicContentController } from './playlist-deterministic-content-controller';
import RegionContentController from './region-content-controller';

/**
 * @import { ContentController, ContentControllerOpts, ContentControllerFactory as IContentControllerFactory, PlaylistContentControllerOpts } from '.';
 * @import BrowserCapabilities from '../../capabilities/browser-capabilities';
 * @import { Logger } from '../../logger/logger';
 * @import MediumExternalSourceFactory from '../../medium-external/medium-external-source-factory';
 * @import PlaybackControlsEmitter from '../../playback-controls/playback-controls-emitter';
 * @import { PreloadStrategy } from '../../preload-strategy'
 * @import { ScheduleContentInfo } from '../../schedule/schedule-info';
 * @import Store from '../../store/store';
 * @import StretchEnum from '../../stretch/stretch-enum';
 * @import UnixTimestamp from '../../unix-timestamp/unix-timestamp';
 * @import { IVariablesNamespaceRegistry } from '../../variables';
 * @import { ViewerRegistry } from '../../viewer/registry';
 * @import { Content, Layout, Medium } from '../content';
 * @import ContentDownloader from '../downloader/content-downloader';
 */

/**
 * @implements {IContentControllerFactory}
 */
export default class ContentControllerFactory {
  /**
   * @param {BrowserCapabilities} browserCapabilities
   * @param {ContentDownloader} contentDownloader
   * @param {Logger} logger
   * @param {MediumExternalSourceFactory} mediumExternalSourceFactory
   * @param {PlaybackControlsEmitter} playbackControlsEmitter
   * @param {PreloadStrategy} preloadStrategy
   * @param {Store} store
   * @param {UnixTimestamp} unixTimestamp
   * @param {IVariablesNamespaceRegistry} variablesNamespaceRegistry
   * @param {ViewerRegistry} viewerRegistry
   */
  constructor(
    browserCapabilities,
    contentDownloader,
    logger,
    mediumExternalSourceFactory,
    playbackControlsEmitter,
    preloadStrategy,
    store,
    unixTimestamp,
    variablesNamespaceRegistry,
    viewerRegistry
  ) {
    this.browserCapabilities = browserCapabilities;
    this.contentDownloader = contentDownloader;
    this.logger = logger;
    this.mediumExternalSourceFactory = mediumExternalSourceFactory;
    this.playbackControlsEmitter = playbackControlsEmitter;
    this.preloadStrategy = preloadStrategy;
    this.store = store;
    this.unixTimestamp = unixTimestamp;
    this.variablesNamespaceRegistry = variablesNamespaceRegistry;
    this.viewerRegistry = viewerRegistry;
  }

  /**
   * @param {HTMLElement} el
   * @param {Content} content
   * @param {ContentControllerOpts} opts
   * @returns {ContentController}
   */
  newMedium(el, content, opts) {
    const medium = /** @type {Medium} */ (content.medium);
    switch (medium.type) {
      case 'application/x-kiwi-local-1':
        return new MediumExternalContentController(
          this,
          opts.contentInfo,
          content,
          this.contentDownloader,
          this.logger,
          this.mediumExternalSourceFactory,
          el,
          opts.volume,
          this.store,
          this.viewerRegistry
        );
      default:
        break;
    }

    const viewerType = this.viewerRegistry.find(medium.type) ?? undefined;
    return new MediumContentController(
      this.browserCapabilities,
      content,
      opts.contentInfo,
      el,
      new ScheduleInfo({
        trackDuration: opts.trackDuration ?? content.medium?.playDuration,
        medium,
        stretchEnum: getStretchEnum(opts.contentInfo, medium),
        variablesNamespaceRegistry: this.variablesNamespaceRegistry,
        viewerType,
        volume: opts.volume
      }),
      viewerType
    );
  }

  /**
   * @param {HTMLElement} el
   * @param {Content} content
   * @param {PlaylistContentControllerOpts} opts
   * @returns {ContentController}
   */
  newPlaylist(el, content, opts) {
    if (content.playlist?.deterministic) {
      return new PlaylistDeterministicContentController(
        this,
        opts.contentInfo,
        content,
        this.logger,
        this.preloadStrategy,
        el,
        opts.volume,
        this.unixTimestamp,
        this.variablesNamespaceRegistry
      );
    }
    if (opts.disableAutoRewind === undefined) {
      throw new AssertionError(
        'disableAutoRewind is required options for PlaylistContentController'
      );
    }
    return new PlaylistContentController(
      content,
      this,
      opts.contentInfo,
      opts.disableAutoRewind,
      this.logger,
      this.preloadStrategy,
      el,
      opts.volume,
      this.playbackControlsEmitter,
      this.unixTimestamp,
      this.variablesNamespaceRegistry
    );
  }

  /**
   * @private
   * @param {HTMLElement} el
   * @param {Content} content
   * @param {ContentControllerOpts & {region: boolean}} opts
   * @returns {{contentController: ContentController, duration: number}}
   */
  contentControllerFromContent(el, content, opts) {
    switch (ScheduleContent.type(content)) {
      case ContentType.Layout:
        return {
          contentController: new LayoutContentController(
            this,
            opts.contentInfo,
            content,
            el,
            opts.volume
          ),
          duration: /** @type {Layout} */ (content.layout).playDuration
        };
      case ContentType.Medium:
        return {
          contentController: this.newMedium(el, content, opts),
          duration: /** @type {Medium} */ (content.medium).playDuration
        };
      case ContentType.Playlist:
        return {
          contentController: this.newPlaylist(el, content, {
            ...opts,
            // Pro region je se disableAutoRewind nastavuje na false, aby se prováděl automatický rewind
            // na začátek playlistu. Autorewind by mohl dělat i RegionContentController, ale pak by první
            // content nikdy nebyl preloadovaný. Pro playlist mimo region je disableAutoRewind nastaven
            // na true, aby playlist po každé iteraci mohl emitovat event ended.
            disableAutoRewind: !opts.region
          }),
          duration: 0
        };
      default:
        throw new AssertionError('Unknown content type.');
    }
  }

  /** @type {IContentControllerFactory['create']} */
  create(el, content, opts) {
    if (!content) {
      return new EmptyContentController();
    }

    const { contentController } = this.contentControllerFromContent(
      el,
      content,
      { ...opts, region: false }
    );
    return contentController;
  }

  /** @type {IContentControllerFactory['createForRegion']} */
  createForRegion(el, content, opts) {
    if (!content) {
      return new EmptyContentController();
    }
    const volume = opts.volume * ScheduleContent.volume(content);
    const { duration, contentController } = this.contentControllerFromContent(
      el,
      content,
      { ...opts, region: true, volume }
    );

    return new RegionContentController(
      content,
      contentController,
      duration,
      el
    );
  }
}

/**
 * Zjistí roztažení, které se má nastavit médiu.
 * @param {ScheduleContentInfo} contentInfo
 * @param {Medium} medium
 * @returns {StretchEnum} Roztažení.
 */
export function getStretchEnum(contentInfo, medium) {
  const mediumStretchEnum = medium.stretchEnum;
  if (!isNil(mediumStretchEnum)) {
    return mediumStretchEnum;
  }
  return contentInfo.stretchEnum;
}
